/**
 * GEDCOM Multi-Line Processor
 *
 * Handles CONC (concatenation) and CONT (continuation) tags in GEDCOM files.
 * - CONC: Continues the previous line's value without a line break
 * - CONT: Continues the previous line's value with a line break
 *
 * Part of Phase 4 - Slice 10: Multi-Line Values + Edge Cases
 */

import type { TreeNode } from '../types/TreeNode';

/**
 * Process CONC and CONT tags in a tree node and its children
 *
 * This function recursively processes a tree, merging CONC/CONT values
 * with their parent node's value.
 *
 * @param node - The tree node to process
 * @returns The processed node with merged multi-line values
 */
export const processMultiLineValues = (node: TreeNode): TreeNode => {
  // If no children, nothing to process
  if (!node.children || node.children.length === 0) {
    return node;
  }

  const processedChildren: TreeNode[] = [];
  let mergedValue = node.value || '';

  for (const child of node.children) {
    if (child.tag === 'CONC') {
      // CONC: Concatenate without line break
      // Note: GEDCOM spec says CONC should continue at the exact character,
      // but many implementations add a space. We'll handle both.
      if (child.value) {
        // If the parent value ends with a space or the CONC value starts with a space,
        // don't add an extra space
        if (mergedValue && !mergedValue.endsWith(' ') && !child.value.startsWith(' ')) {
          mergedValue += ' ';
        }
        mergedValue += child.value;
      }
    } else if (child.tag === 'CONT') {
      // CONT: Continue with a line break
      if (child.value !== null) {
        mergedValue += '\n' + (child.value || '');
      } else {
        // Empty CONT means just add a newline
        mergedValue += '\n';
      }
    } else {
      // Not a CONC/CONT tag, process it recursively and keep it
      processedChildren.push(processMultiLineValues(child));
    }
  }

  // Return the node with merged value and processed children
  return {
    ...node,
    value: mergedValue || node.value,
    children: processedChildren
  };
};

/**
 * Process all multi-line values in a GEDCOM tree
 *
 * @param tree - Array of root tree nodes
 * @returns Processed tree with merged multi-line values
 */
export const processMultiLineTree = (tree: TreeNode[]): TreeNode[] => {
  return tree.map(node => processMultiLineValues(node));
};

/**
 * Check if a line is a CONC or CONT tag
 *
 * @param tag - The tag to check
 * @returns True if the tag is CONC or CONT
 */
export const isMultiLineTag = (tag: string): boolean => {
  return tag === 'CONC' || tag === 'CONT';
};